import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    image: string;
    question: string;
    options: OptionData[];
}

interface OptionData {
    text: string;
    correct: boolean;
}


const ChooseCorrectHome: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'ChooseCorrectHome.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/ChooseCorrectHome.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "My Home – Choose the Correct Answer Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: any) => {
        console.log(selectedMyOption)
        setSelectedOption(selectedMyOption);
        if (selectedMyOption.correct === true) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Interactive UKG worksheet to help children choose correct answers related to home and its parts. Enhance vocabulary and awareness about rooms and household items." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/my-home/choose-correct-answer" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord chooseImage">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>My Home – Choose the Correct Answer</h1>

                                    <p>
                                        Help your UKG child learn about their surroundings with this engaging “<strong>choose the correct answer</strong>” worksheet on the topic <strong>My Home</strong>. Designed to develop general awareness, this worksheet reinforces vocabulary and identification of different parts of a home.
                                    </p>

                                    <h2>🏠 What Will Kids Learn?</h2>
                                    <ul>
                                        <li>Names and purposes of different rooms in a house</li>
                                        <li>Common household objects</li>
                                        <li>Basic home safety and hygiene habits</li>
                                        <li>Improved vocabulary and comprehension</li>
                                    </ul>

                                    <h2>📝 Worksheet Overview</h2>
                                    <p>
                                        Each question presents a simple statement or image and asks children to <strong>choose the correct answer</strong> from given options. These questions are age-appropriate for UKG-level students and focus on real-life examples.
                                    </p>

                                    <h2>🔍 Sample Questions</h2>
                                    <ul>
                                        <li>Which room do we sleep in?
                                            <br />A. Kitchen &nbsp;&nbsp; B. Bedroom &nbsp;&nbsp; C. Bathroom</li>
                                        <li>We cook food in the:
                                            <br />A. Kitchen &nbsp;&nbsp; B. Living Room &nbsp;&nbsp; C. Bedroom</li>
                                        <li>Which of these is found in the bathroom?
                                            <br />A. Sofa &nbsp;&nbsp; B. Mirror &nbsp;&nbsp; C. Bed</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>👨‍🏫 Teaching Tips</h2>
                                    <ul>
                                        <li>Encourage your child to walk around the house and identify different rooms and their uses.</li>
                                        <li>Use real objects from home to reinforce the words in the worksheet.</li>
                                        <li>Discuss household items and where they belong to improve memory and observation.</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>My Home – Choose the Correct Answer</h1>

                                <p>
                                    Help your UKG child learn about their surroundings with this engaging “<strong>choose the correct answer</strong>” worksheet on the topic <strong>My Home</strong>. Designed to develop general awareness, this worksheet reinforces vocabulary and identification of different parts of a home.
                                </p>

                                <h2>🏠 What Will Kids Learn?</h2>
                                <ul>
                                    <li>Names and purposes of different rooms in a house</li>
                                    <li>Common household objects</li>
                                    <li>Basic home safety and hygiene habits</li>
                                    <li>Improved vocabulary and comprehension</li>
                                </ul>

                                <h2>📝 Worksheet Overview</h2>
                                <p>
                                    Each question presents a simple statement or image and asks children to <strong>choose the correct answer</strong> from given options. These questions are age-appropriate for UKG-level students and focus on real-life examples.
                                </p>

                                <h2>🔍 Sample Questions</h2>
                                <ul>
                                    <li>Which room do we sleep in?
                                        <br />A. Kitchen &nbsp;&nbsp; B. Bedroom &nbsp;&nbsp; C. Bathroom</li>
                                    <li>We cook food in the:
                                        <br />A. Kitchen &nbsp;&nbsp; B. Living Room &nbsp;&nbsp; C. Bedroom</li>
                                    <li>Which of these is found in the bathroom?
                                        <br />A. Sofa &nbsp;&nbsp; B. Mirror &nbsp;&nbsp; C. Bed</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👨‍🏫 Teaching Tips</h2>
                                <ul>
                                    <li>Encourage your child to walk around the house and identify different rooms and their uses.</li>
                                    <li>Use real objects from home to reinforce the words in the worksheet.</li>
                                    <li>Discuss household items and where they belong to improve memory and observation.</li>
                                </ul>
                            </article>
                            <div className="mt-4 mb-4">
                                <div className="half-width text-center">
                                    <img width="50%" className="img-thumbnail" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} />
                                </div>
                                <h3 className="mt-4 mb-2">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>
                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value)}>
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem key={index}
                                        className={`grid-item ${selectedOption?.text === option.text
                                            ? option.correct === true
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}>
                                        <IonRadio slot="start" value={option} />
                                        <IonLabel>{option.text}</IonLabel>
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                            <div className="mb-5"></div>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default ChooseCorrectHome;
